import { PlusOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { FC, type ChangeEvent } from "react";
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Space,
  Spin,
  Typography,
} from "antd";
import type { FormInstance } from "antd";
import ParcelDestinationAddressList from "../destination/ParcelDestinationAddressList";
import ParcelCreateAddressModal from "../destination/ParcelCreateAddressModal";
import ParcelPhonePickerModal from "../destination/ParcelPhonePickerModal";
import { sanitizeParcelPhone } from "../destination/parcelPhoneSearchUtils";
import { useParcelDestinationSearch } from "../hooks/useParcelDestinationSearch";
import ParcelDestinationAddressFields from "../destination/ParcelDestinationAddressFields";
import {
  ParcelFormSectionHeader,
  ParcelFormSectionTitle,
  ParcelFormZone,
  ParcelHintBox,
  ParcelSearchAgainBox,
  PARCEL_DESTINATION_ADDRESS_MAX_LENGTH,
  PARCEL_DESTINATION_FULLNAME_MAX_LENGTH,
} from "../../manageParcelLayout";

const { Text } = Typography;

const ORDER_SEARCH_INPUT_WIDTH = 300;
const ORDER_SEARCH_INPUT_HEIGHT = 32;

type ParcelDestinationStepProps = {
  form: FormInstance;
  onAddressChange?: () => void;
  onCancel?: () => void;
};

const digitsOnlyPhone = (e: ChangeEvent<HTMLInputElement>) =>
  sanitizeParcelPhone(e?.target?.value ?? "");

const ParcelDestinationStep: FC<ParcelDestinationStepProps> = ({
  form,
  onAddressChange,
  onCancel,
}) => {
  const [formAddress] = Form.useForm();

  const {
    loading,
    searchPhone,
    phonePickerOpen,
    setPhonePickerOpen,
    phonePickerGroups,
    radioSelect,
    createModalOpen,
    setCreateModalOpen,
    lastSearchedDigits,
    isSearchStale,
    displayItems,
    canCreateShipAddress,
    createModalPhone,
    createModalPhoneEditable,
    addressEmptyDescription,
    phoneHintText,
    searchAgainMessage,
    handleSearchPhoneChange,
    handleSearchClick,
    handlePhoneGroupPick,
    handleCreateModalFinish,
    handleSearchFromCreateModal,
    selectAddress,
  } = useParcelDestinationSearch({ form, onAddressChange });

  return (
    <ParcelFormZone>
      <ParcelFormSectionHeader>
        <ParcelFormSectionTitle style={{ marginBottom: 0 }}>
          1. ที่อยู่ผู้รับ (ปลายทาง)
        </ParcelFormSectionTitle>
        {onCancel ? (
          <Button size="small" onClick={onCancel}>
            ยกเลิก
          </Button>
        ) : null}
      </ParcelFormSectionHeader>

      <Form.Item name={["destination", "telephone1"]} hidden>
        <Input />
      </Form.Item>

      <PhoneFormItem label="ใส่เบอร์โทรเพื่อเพิ่มหรือค้นหาลูกค้า" labelAlign="left">
        <Space.Compact>
          <Input
            style={{
              height: ORDER_SEARCH_INPUT_HEIGHT,
              width: ORDER_SEARCH_INPUT_WIDTH,
            }}
            placeholder="ค้นหา เบอร์โทรศัพท์"
            maxLength={10}
            inputMode="numeric"
            value={searchPhone}
            onChange={(e) => handleSearchPhoneChange(digitsOnlyPhone(e))}
            onPressEnter={() => void handleSearchClick()}
          />
          <Button type="primary" loading={loading} onClick={() => void handleSearchClick()}>
            เพิ่ม/ค้นหา
          </Button>
        </Space.Compact>
      </PhoneFormItem>

      {isSearchStale ? (
        <ParcelSearchAgainBox>
          <Text>{searchAgainMessage}</Text>
        </ParcelSearchAgainBox>
      ) : null}

      {phoneHintText ? (
        <ParcelHintBox>
          <Text type="secondary">{phoneHintText}</Text>
        </ParcelHintBox>
      ) : null}

      <HiddenDestinationFields form={form} />

      <Divider style={{ margin: "12px 0 16px" }} />

      <HeadWrapper>
        <SectionTitle underline>ที่อยู่ในการจัดส่ง</SectionTitle>
        <ButtonWrapper>
          <StyledButton
            type="primary"
            disabled={!canCreateShipAddress}
            onClick={() => setCreateModalOpen(true)}
          >
            <PlusOutlined /> เพิ่มที่อยู่จัดส่งเพิ่มเติม
          </StyledButton>
        </ButtonWrapper>
      </HeadWrapper>

      <Spin spinning={loading} tip="กำลังค้นหาที่อยู่...">
        <Row>
          <Col span={24}>
            <ParcelDestinationAddressList
              items={displayItems}
              radioSelect={radioSelect}
              onRadioChange={selectAddress}
              emptyDescription={addressEmptyDescription}
            />
          </Col>
        </Row>
      </Spin>

      <ParcelPhonePickerModal
        open={phonePickerOpen}
        groups={phonePickerGroups}
        onClose={() => setPhonePickerOpen(false)}
        onSelect={handlePhoneGroupPick}
      />

      <ParcelCreateAddressModal
        open={createModalOpen}
        phone={createModalPhone}
        lastSearchedDigits={lastSearchedDigits}
        isSearchStale={isSearchStale}
        phoneEditable={createModalPhoneEditable}
        form={formAddress}
        onClose={() => {
          setCreateModalOpen(false);
          formAddress.resetFields();
        }}
        onFinish={handleCreateModalFinish}
        onSearchFromModal={(phoneDigits) => void handleSearchFromCreateModal(phoneDigits)}
      />
    </ParcelFormZone>
  );
};

const HiddenDestinationFields: FC<{ form: FormInstance }> = ({ form }) => (
  <div aria-hidden style={{ display: "none" }}>
    <Form.Item name={["destination", "address_id"]} hidden>
      <Input />
    </Form.Item>
    <Form.Item
      name={["destination", "fullname"]}
      rules={[
        { required: true, message: "กรุณาเลือกหรือสร้างที่อยู่ผู้รับ" },
        {
          max: PARCEL_DESTINATION_FULLNAME_MAX_LENGTH,
          message: `ชื่อผู้รับต้องไม่เกิน ${PARCEL_DESTINATION_FULLNAME_MAX_LENGTH} ตัวอักษร`,
        },
      ]}
    >
      <Input />
    </Form.Item>
    <Form.Item
      name={["destination", "address"]}
      rules={[
        { required: true, message: "กรุณาเลือกหรือสร้างที่อยู่ผู้รับ" },
        {
          max: PARCEL_DESTINATION_ADDRESS_MAX_LENGTH,
          message: `ที่อยู่ต้องไม่เกิน ${PARCEL_DESTINATION_ADDRESS_MAX_LENGTH} ตัวอักษร`,
        },
      ]}
    >
      <Input />
    </Form.Item>
    <ParcelDestinationAddressFields
      form={form}
      namePrefix={["destination"]}
      fieldsDisabled
    />
    <Form.Item
      name={["destination", "postcode"]}
      rules={[{ required: true, message: "กรุณาเลือกตำบลเพื่อรับรหัสไปรษณีย์" }]}
    >
      <Input readOnly />
    </Form.Item>
  </div>
);

export default ParcelDestinationStep;

const PhoneFormItem = styled(Form.Item)`
  margin-bottom: 10px;
  width: 100%;
`;

const HeadWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 10px;
  margin-bottom: 10px;

  & > :first-child {
    flex: 3;
  }

  & > :last-child {
    flex: 7;
    display: flex;
    justify-content: flex-end;
  }
`;

const SectionTitle = styled(Typography.Text)`
  font-weight: 600;
  white-space: nowrap;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const StyledButton = styled(Button)`
  margin-right: 5px;
`;
